import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { usePartnerScoreGlobalFilter } from '@components/partner';
import { CubeQuery } from '@/types/cube';
import useQuery from '@/api/queries/useQuery';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { PartnerScoreCacheKeys } from '@api/hcp/partnerscore/cacheKeys';
import { toNumberOrNull, toStringOrNull } from '@/utils/cube/mapper';
import { PARTNERSCORE_CUBE_NAME } from '@api/hcp';

export type PartnerScoreSurvey = {
  id?: string | null;
  title: string | null;
  year: number | null;
  quarter: number | null;
  country: string | null;
};

export default function useGetPartnerScoreSurveyList({
  year,
  quarter,
}: {
  year: number;
  quarter: number;
}): UseQueryResult<Array<PartnerScoreSurvey>, AxiosError> {
  const { countries } = usePartnerScoreGlobalFilter();

  const query: CubeQuery = {
    dimensions: [
      `${PARTNERSCORE_CUBE_NAME}.quarter`,
      `${PARTNERSCORE_CUBE_NAME}.year`,
      `${PARTNERSCORE_CUBE_NAME}.country_code`,
      `${PARTNERSCORE_CUBE_NAME}.survey_title`,
      `${PARTNERSCORE_CUBE_NAME}.survey_id`,
    ],
    filters: [
      {
        member: `${PARTNERSCORE_CUBE_NAME}.country_code`,
        operator: 'equals',
        values: countries,
      },
      {
        member: `${PARTNERSCORE_CUBE_NAME}.survey_title`,
        operator: 'set',
      },
      ...(year
        ? [
            {
              member: `${PARTNERSCORE_CUBE_NAME}.year`,
              operator: 'equals' as const,
              values: [year.toString()],
            },
          ]
        : []),
      ...(quarter && quarter >= 1
        ? [
            {
              member: `${PARTNERSCORE_CUBE_NAME}.quarter`,
              operator: 'equals' as const,
              values: [quarter.toString()],
            },
          ]
        : []),
    ],
    order: { [`${PARTNERSCORE_CUBE_NAME}.year`]: 'desc', [`${PARTNERSCORE_CUBE_NAME}.quarter`]: 'asc' },
    limit: 10000,
  };

  return useQuery<CubeRawRow[], AxiosError, Array<PartnerScoreSurvey>>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.partnerScoreSurveyList(query)],
      select: data => {
        return data.map(survey => ({
          id: toStringOrNull(survey[`${PARTNERSCORE_CUBE_NAME}.survey_id`]),
          title: toStringOrNull(survey[`${PARTNERSCORE_CUBE_NAME}.survey_title`]),
          country: toStringOrNull(survey[`${PARTNERSCORE_CUBE_NAME}.country_code`]),
          year: toNumberOrNull(survey[`${PARTNERSCORE_CUBE_NAME}.year`]),
          quarter: toNumberOrNull(survey[`${PARTNERSCORE_CUBE_NAME}.quarter`]),
        }));
      },
      enabled: true,
    },
  });
}
